import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { useLocation } from 'react-router-dom'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { getMatchingPaymentTickets } from '../../core/_requests'
import {
  PrepareMatchingPaymentTicketsToObject,
  MatchingPaymentFinishedTicketsRequestParams,
  MatchingFile,
  MatchingPaymentActiveTicketsFilters,
  MatchingPaymentActiveTicketsRequestParams,
  PaginationState,
  matchingCapabilities,
  getVisibleActivePaymentColumns,
  canUser,
} from '../../core/_models'
import ActivePaymentTicketsFilter from './ActivePaymentTicketsFilter'
import ActivePaymentTicketItem from './ActivePaymentTicketItem'
import { useAuth } from '../../../auth'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import '../../../../assets/css/pagination.css'
import { Forbidden } from '../common/Forbidden'
import { matchingActivePaymentsConfig as config } from './config'
import { Header } from '../common/Header'
import { Alert } from '../../../../../components/Alert/Alert'
import { EMatchingPaymentTicketStatus } from '../../../../../types/enums'

const { breadcrumbs, defaultFilters, defaultList, perPage, relevantStatusList } = config

const ActivePaymentTickets = () => {
  const { currentUser } = useAuth()

  // Права и видимость
  const canAccess = useMemo(
    () => canUser(currentUser, matchingCapabilities.canViewActivePayments),
    [currentUser]
  )
  const canManageBills = useMemo(
    () => canAccess && canUser(currentUser, matchingCapabilities.canEditTickets),
    [currentUser, canAccess]
  )
  const canManageTickets = useMemo(
    () => canAccess && canUser(currentUser, matchingCapabilities.canEditTickets),
    [currentUser, canAccess]
  )
  const canCreateDispute = useMemo(
    () => canAccess && canUser(currentUser, matchingCapabilities.canCreateDispute),
    [currentUser, canAccess]
  )
  const visibility = useMemo(() => getVisibleActivePaymentColumns(currentUser), [currentUser])
  const visibleColumns = useMemo(() => Object.keys(visibility).filter(Boolean), [visibility])

  // Токен тикета из адресной строки (?ticket=...) — deep-link из дашборда мэтчинга.
  const { search: locationSearch } = useLocation()
  const ticketFromUrl = useMemo(
    () => new URLSearchParams(locationSearch).get('ticket') || undefined,
    [locationSearch]
  )

  // Состояния
  const [filters, setFilters] = useState<MatchingPaymentActiveTicketsFilters>(defaultFilters)
  const [tickets, setTickets] = useState(defaultList)
  const [isLoading, setIsLoading] = useState(false)
  const [countPages, setCountPages] = useState(0)
  const [fullCount, setFullCount] = useState(0)
  const [showAlert, setShowAlert] = useState(false)

  /**
   * Загрузка списка тикетов
   */
  const loadTicketList = useCallback(
    async (query: MatchingPaymentFinishedTicketsRequestParams) => {
      if (!canAccess) return

      setIsLoading(true)
      try {
        const response = await getMatchingPaymentTickets(query)

        if (!response?.data?.success) {
          throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
          )
        }

        const tickets = PrepareMatchingPaymentTicketsToObject(response?.data?.list || [])
        const totalRows = response.data?.totalRows || 0
        const countPages = Math.ceil(totalRows / perPage)

        setTickets(tickets || {})
        setCountPages(countPages)
        setFullCount(totalRows)
      } catch (error) {
        console.error('Ошибка при загрузке тикетов:', error)
        setTickets({})
        setCountPages(0)
        setFullCount(0)
      } finally {
        setIsLoading(false)
      }
    },
    [canAccess]
  )

  /**
   * Применение фильтров (подготовка запроса и отправка запроса на сервер)
   */
  const applyFilters = useCallback(
    (newFilters: MatchingPaymentActiveTicketsFilters, pagination?: PaginationState) => {
      if (!canAccess) return
      const filters = { ...defaultFilters, ...newFilters }
      setFilters({ ...filters })

      // Формируем query только из поддерживаемых swagger-полей
      const query: MatchingPaymentActiveTicketsRequestParams = {
        ...defaultFilters,
        page: pagination?.page || 1,
        perPage: pagination?.perPage || perPage,
        statusList: relevantStatusList,
        search: filters?.search || undefined,
      }

      loadTicketList(query)
    },
    [loadTicketList, canAccess]
  )

  /**
   * Обновление статуса чека
   */
  const updateBillStatus = useCallback(
    (ticketToken: string, billId: MatchingFile['id'], status: MatchingFile['status']) => {
      if (!canManageBills) return

      if (!ticketToken || !billId || !status) {
        console.warn('Некорректные параметры обновления статуса чека')
        return
      }

      const ticketsClone = { ...tickets }

      const ticket = ticketsClone?.[ticketToken]
      if (!ticket) return

      const bills = [...(ticket?.files || [])]

      const billIndex = bills.findIndex((file) => file?.id === billId)
      if (billIndex === undefined) return

      bills[billIndex] = { ...bills[billIndex], status }

      ticketsClone[ticketToken] = { ...ticket, files: [...bills], status: EMatchingPaymentTicketStatus.FILES_VERIFICATION }
      setTickets(ticketsClone)
    },
    [tickets, canManageBills]
  )

  /**
   * Добавляем чек в тикет
   */
  const updateBillForTicket = useCallback(
    (ticketToken: string, bill: MatchingFile) => {
      if (!canManageTickets) return

      if (!ticketToken || !bill) {
        console.warn('Некорректные параметры обновления статуса чека')
        return
      }
      
      setTickets(prev => {
        const ticket = prev?.[ticketToken];
        if (!ticket) return prev;

        const bills = [...(ticket.files || []), bill];

        return {
          ...prev,
          [ticketToken]: { ...ticket, files: bills },
        };
      });
    },
    [tickets, canManageTickets]
  )

  /**
   * Обновление запрошенную сумму
   */
  const updateExpectedAmount = useCallback(
    (ticketToken: string, amount: number, requisiteAmount?: number | null) => {
      if (!canManageTickets) return

      if (!ticketToken || !amount) {
        console.warn('Некорректные параметры обновления запрошеной суммы')
        return
      }

      const ticketsClone = { ...tickets }

      const ticket = ticketsClone?.[ticketToken]
      if (!ticket) return

      ticketsClone[ticketToken] = {
        ...ticket,
        expectedAmount: amount,
        requisiteAmount: requisiteAmount !== undefined ? requisiteAmount : ticket.requisiteAmount,
      }
      setTickets(ticketsClone)
    },
    [tickets, canManageTickets]
  )

  /**
   * CPAYMENT-1615: после создания диспута строка обязана сразу стать замороженной — подсветка,
   * бейдж, снятые действия с чеком. Обновляем точечно, а не перезапрашиваем список: перезапрос
   * переставил бы очередь под руками у оператора, который с ней прямо сейчас работает.
   */
  const markTicketDisputed = useCallback(
    (ticketToken: string) => {
      if (!ticketToken) {
        console.warn('Некорректные параметры пометки тикета диспутом')
        return
      }

      const ticketsClone = { ...tickets }

      const ticket = ticketsClone?.[ticketToken]
      if (!ticket) return

      ticketsClone[ticketToken] = { ...ticket, disputeStatus: 'OPEN' }
      setTickets(ticketsClone)
    },
    [tickets]
  )

  /**
   * Удаление тикета из списка
   */
  const removeTicketFromList = useCallback(
    (ticketToken: string) => {
      if (!canManageTickets) return

      const ticketsClone = { ...tickets }
      delete ticketsClone[ticketToken]
      setTickets(ticketsClone)
      setFullCount((prev) => Math.max(0, prev - 1))
    },
    [tickets, canManageTickets]
  )

  /**
   * Инициализация списка
   */
  useEffect(() => {
    applyFilters({ ...defaultFilters, search: ticketFromUrl }, { page: 1, perPage })
  }, [applyFilters, ticketFromUrl])

  return (
    <div className='card mb-7'>
      <PageTitle breadcrumbs={breadcrumbs}>Активные пополнения</PageTitle>
      <Header title='Активные пополнения' count={fullCount} />

      {!canAccess && <Forbidden />}

      {canAccess && (
        <ActivePaymentTicketsFilter
          defaultFilters={defaultFilters}
          applyFilters={applyFilters}
          visibility={visibility}
          initialSearch={ticketFromUrl}
        />
      )}

      {canAccess && (
        <div className='card-body py-3'>
          <Alert isOpen={showAlert} content='Скопировано' status='success' />
          <Table>
            <TableHead>
              <tr>
                {visibility.ticketToken && (
                  <th className='w-300px min-w-120px align-top'>
                    ID тикета <br />
                    <small>Токен UUID</small>
                  </th>
                )}
                {visibility.transactionId && (
                  <th className='w-200px min-w-130px align-top'>
                    ID&nbsp;транзакции <br />
                    <small>ID мерчанта</small>
                  </th>
                )}
                {visibility.createdAt && <th className='min-w-120px align-top'>Время создания</th>}
                {visibility.amount && <th className='min-w-100px align-top'>Сумма к переводу</th>}
                {visibility.user && <th className='min-w-100px align-top'>Клиент</th>}
                {visibility.merchant && <th className='min-w-150px align-top'>Мерчант</th>}
                {visibility.paymentMethod && (
                  <th className='min-w-150px align-top'>
                    Платежный&nbsp;реквизит <br />
                    <small>Платежный метод</small>
                  </th>
                )}
                {visibility.files && <th className='min-w-80px text-center align-top'>Чек и его Статус</th>}
                {visibility.antifraud && <th className='min-w-60px text-center align-top'>Антифрод</th>}
                <th className='min-w-150px'></th>
              </tr>
            </TableHead>
            <TableBody>
              {Object.keys(tickets).length > 0 ? (
                Object.keys(tickets).map((ticketToken, index) => (
                  <ActivePaymentTicketItem
                    key={ticketToken}
                    ticket={tickets[ticketToken]}
                    setShowAlert={setShowAlert}
                    currentUser={currentUser}
                    updateBillStatus={updateBillStatus}
                    removeTicketFromList={removeTicketFromList}
                    updateBillForTicket={updateBillForTicket}
                    visibility={visibility}
                    visibleColumns={visibleColumns}
                    canManageBills={canManageBills}
                    canManageTickets={canManageTickets}
                    canCreateDispute={canCreateDispute}
                    markTicketDisputed={markTicketDisputed}
                    updateExpectedAmount={updateExpectedAmount}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={(visibleColumns?.length || 0) + 1} className='text-center'>
                    {isLoading ? 'Загрузка...' : 'Тикеты не найдены'}
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>

          {countPages > 1 && (
            <div className='d-flex justify-content-center mt-5'>
              <ReactPaginate
                breakLabel='...'
                nextLabel='Следующая >'
                onPageChange={(selectedItem) =>
                  applyFilters({ ...filters }, { page: selectedItem.selected + 1, perPage })
                }
                pageRangeDisplayed={5}
                pageCount={countPages}
                previousLabel='< Предыдущая'
                renderOnZeroPageCount={() => null}
                className='pagination'
                activeClassName='active'
                disabledClassName='disabled'
                nextClassName='page-item'
                nextLinkClassName='page-link'
                pageClassName='page-item'
                pageLinkClassName='page-link'
                previousClassName='page-item'
                previousLinkClassName='page-link'
                breakClassName='page-item'
                breakLinkClassName='page-link'
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ActivePaymentTickets
